import type { ComponentType } from 'react'
import { Badge } from './Badge'

type BadgeVariant = 'default' | 'ok' | 'warn' | 'err' | 'accent' | 'info'

interface EntityBadge {
  label: string
  variant: BadgeVariant
  dot?: boolean
}

interface EntityMetric {
  icon: ComponentType<{ size?: number; style?: React.CSSProperties }>
  label: string
  value: string | number
  accent?: boolean
  onClick?: () => void
}

interface EntityAction {
  label: string
  icon?: ComponentType<{ size?: number; style?: React.CSSProperties }>
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'ghost'
}

interface EntityHeaderProps {
  icon: ComponentType<{ size?: number; style?: React.CSSProperties }>
  title: string
  badges?: EntityBadge[]
  metrics?: EntityMetric[]
  actions?: EntityAction[]
}

export function EntityHeader({ icon: Icon, title, badges, metrics, actions }: EntityHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24,
        animation: 'in-up 400ms var(--ease) 80ms both',
      }}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--r-m)',
              background: 'linear-gradient(135deg, var(--accent-m), var(--bg-3))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--border)',
              flexShrink: 0,
            }}
          >
            <Icon size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 600,
              color: 'var(--text-1)',
              fontFamily: 'var(--font)',
              letterSpacing: '-.01em',
            }}
          >
            {title}
          </h1>
          {badges?.map((b, i) => (
            <Badge key={i} v={b.variant} dot={b.dot}>
              {b.label}
            </Badge>
          ))}
        </div>
        {metrics && metrics.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 20,
              fontSize: 13,
              color: 'var(--text-2)',
              fontFamily: 'var(--font)',
              paddingLeft: 52,
            }}
          >
            {metrics.map((m, i) => (
              <span
                key={i}
                onClick={m.onClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  ...(m.onClick && {
                    cursor: 'pointer',
                    borderRadius: 'var(--r-s)',
                    padding: '2px 6px',
                    margin: '-2px -6px',
                    transition: 'color 100ms ease',
                  }),
                  ...(m.accent && {
                    fontFamily: 'var(--mono)',
                    fontWeight: 600,
                    color: 'var(--accent)',
                  }),
                }}
                onMouseEnter={m.onClick ? e => { e.currentTarget.style.color = 'var(--accent)' } : undefined}
                onMouseLeave={m.onClick ? e => { e.currentTarget.style.color = '' } : undefined}
              >
                <m.icon size={14} />
                {typeof m.value === 'number' ? m.value.toLocaleString() : m.value}
                {!m.accent && m.label ? ` ${m.label}` : ''}
              </span>
            ))}
          </div>
        )}
      </div>
      {actions && actions.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {actions.map((a, i) => {
            const variant = a.variant || 'secondary'
            const cls =
              variant === 'primary'
                ? 'btn-primary'
                : variant === 'ghost'
                  ? 'btn-ghost'
                  : 'btn-secondary'
            return (
              <button
                key={i}
                className={cls}
                onClick={a.onClick}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {a.icon && <a.icon size={15} />}
                {a.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
